import { ChartContainer } from "@backpac/monica";
import type * as React from "react";
import { useRef } from "react";
import { AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import type recharts from "recharts";

// To prevent the tooltip from being cut off, apply a negative margin-right to the container and a positive padding-right to the container.
const YAXIS_RIGHT_OFFSET = 16;

// Base chart props that are common to all chart types
export type BaseChartProps = {
  data: { date: string; [key: string]: string | number }[];
  formatValue: (value: number) => string;
  dateType?: "month" | "day";
  children?: React.ReactNode;
};

export function BaseChart({ data, formatValue, dateType = "day", children }: BaseChartProps) {
  const xAxisKey = "date";
  const xAxisLabelFormatter = (value: string) =>
    dateType === "month" ? value : new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" });

  const axisLineColor = "#e5e5e5";
  const axisRef = useRef<recharts.YAxis>(null);

  return (
    <ChartContainer className="flex-1">
      <AreaChart data={data} margin={{ left: 0, right: YAXIS_RIGHT_OFFSET, top: 0, bottom: 0 }}>
        <XAxis
          dataKey={xAxisKey}
          axisLine={{ stroke: axisLineColor }}
          tickLine={false}
          ticks={[data[0]?.date, data[data.length - 1]?.date]}
          tickFormatter={xAxisLabelFormatter}
          tick={({ x, y, payload, index }) => {
            const dx = index === 0 ? 0 : 8; // To adjust the position of the X-axis, hardcode the distance value based on the index.

            return (
              <text
                x={x}
                y={y}
                dy={10}
                dx={dx}
                fill="#595959"
                fontSize={10}
                fontWeight={300}
                textAnchor={index === 0 ? "start" : "end"}
              >
                {xAxisLabelFormatter(payload.value)}
              </text>
            );
          }}
        />
        <YAxis
          ref={axisRef}
          axisLine={{ stroke: axisLineColor }}
          tickLine={false}
          tickMargin={0}
          tickCount={5}
          tickFormatter={formatValue}
          style={{ fontSize: "10px", fontWeight: 300, color: "#595959" }}
        />
        <CartesianGrid stroke={axisLineColor} strokeDasharray="2 2" horizontal={true} vertical={false} />

        {children}
      </AreaChart>
    </ChartContainer>
  );
}

// Helper function to format date label for tooltips
export const formatDateLabel = (value: string, dateType: "month" | "day") =>
  dateType === "month"
    ? new Date(value).toLocaleDateString("en-US", { month: "short", year: "numeric" })
    : new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
